import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2, CheckCircle, XCircle, UserPlus } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";

interface AccountResult {
  email: string;
  name: string;
  user_id?: string;
  error?: string;
}

interface ProcessingResults {
  created: AccountResult[];
  errors: AccountResult[];
  total: number;
}

const CreateWaitlistAccounts = () => {
  const { toast } = useToast();
  const [isProcessing, setIsProcessing] = useState(false);
  const [results, setResults] = useState<ProcessingResults | null>(null);

  const handleCreateAccounts = async () => {
    setIsProcessing(true);
    setResults(null);

    try {
      const { data, error } = await supabase.functions.invoke('create-waitlist-accounts');

      if (error) throw error;

      const processingResults = data.results as ProcessingResults;
      setResults(processingResults);

      toast({
        title: "Account Creation Complete",
        description: `Created ${processingResults.created.length} accounts with ${processingResults.errors.length} errors`,
      });

    } catch (error: any) {
      toast({
        title: "Account Creation Failed",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 to-secondary/5 p-8">
      <div className="max-w-4xl mx-auto space-y-6">
        <div>
          <h1 className="text-4xl font-bold mb-2">Create Waitlist Accounts</h1>
          <p className="text-muted-foreground">
            Convert waitlist entries into user accounts with default credentials
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Account Creation</CardTitle>
            <CardDescription>
              This will create user accounts for all waitlist entries that don't have accounts yet.
              Each account will be created with:
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-primary" />
                <span>Email from waitlist entry</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-primary" />
                <span>Default password: <code className="bg-muted px-1 py-0.5 rounded">password123</code></span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-primary" />
                <span>Must change password on first login</span>
              </div>
            </div>

            <Button 
              onClick={handleCreateAccounts} 
              disabled={isProcessing}
              size="lg"
              className="w-full"
            >
              {isProcessing ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <UserPlus className="mr-2 h-4 w-4" />
                  Create Accounts for Waitlist Entries
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        {results && (
          <div className="space-y-4">
            <Alert>
              <AlertTitle>Processing Summary</AlertTitle>
              <AlertDescription>
                Total entries processed: {results.total} | 
                Successfully created: {results.created.length} | 
                Errors: {results.errors.length}
              </AlertDescription>
            </Alert>

            {results.created.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                    Successfully Created ({results.created.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {results.created.map((account, index) => (
                      <div key={index} className="p-3 bg-green-50 dark:bg-green-950/20 rounded-lg border border-green-200 dark:border-green-800">
                        <div className="font-medium">{account.name}</div>
                        <div className="text-sm text-muted-foreground">{account.email}</div>
                        {account.user_id && (
                          <div className="text-xs text-muted-foreground mt-1">
                            User ID: {account.user_id}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {results.errors.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <XCircle className="h-5 w-5 text-destructive" />
                    Errors ({results.errors.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {results.errors.map((error, index) => (
                      <div key={index} className="p-3 bg-destructive/10 rounded-lg border border-destructive/20">
                        <div className="font-medium">{error.name}</div>
                        <div className="text-sm text-muted-foreground">{error.email}</div>
                        <div className="text-sm text-destructive mt-1">{error.error}</div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CreateWaitlistAccounts;
